<template>
  <div class="search-order">
    <div class="el-search vue-top-padding radius">
      <vtm-filter-bar
        ref="fb"
        :fields="fields"
        :search="search"
        :presets="presets"
        :extra-keys="['payState', 'payType', 'payStates', 'payTypes', 'sort', 'order']"
        :date-time="dateTime"
        storage-key="orders"
        @submit="onSubmit"
        @reset="resetSearch"
      >
        <template #lead>
          <el-button
            class="so-export"
            size="small"
            icon="el-icon-printer"
            :loading="exportLoading"
            @click="exportOrder"
            >{{ $t("common.export") }}</el-button
          >
          <!-- 页面附加按钮（如交易页的列设置） -->
          <slot name="extra"></slot>

          <el-date-picker
            class="so-date"
            size="small"
            v-model="dateTime"
            type="datetimerange"
            :picker-options="pickerOptions"
            :default-time="['00:00:00', '23:59:59']"
            range-separator="-"
            :start-placeholder="$t('searchOrder.start')"
            :end-placeholder="$t('searchOrder.end')"
            @change="onSubmit"
          >
          </el-date-picker>
        </template>

        <template #filters>
          <el-select
            class="so-select"
            size="small"
            v-model="search.payStates"
            multiple
            collapse-tags
            :no-data-text="$t('common.noData')"
            :no-match-text="$t('common.noData')"
            clearable
            :placeholder="$t('searchOrder.payState')"
            @change="onSubmit"
          >
            <el-option v-for="item in payStateList" :key="item.id" :label="item.label" :value="item.value"> </el-option>
          </el-select>

          <el-select
            class="so-select"
            size="small"
            v-model="search.payTypes"
            multiple
            collapse-tags
            :no-data-text="$t('common.noData')"
            :no-match-text="$t('common.noData')"
            clearable
            :placeholder="$t('order.payType')"
            @change="onSubmit"
          >
            <el-option v-for="item in payTypeList" :key="item.id" :label="item.label" :value="item.value"> </el-option>
          </el-select>
        </template>
      </vtm-filter-bar>
    </div>
  </div>
</template>

<script>
  import VtmFilterBar from "@/components/common/VtmFilterBar";

  // payState：0 待支付 · 1 已支付 · 2 已接单 · 3 已出货 · -1 待退款 · -2 已退款 · -3 已失效 · -4 已取消
  // 与字典 gp_pay_state 的取值类型一致（字符串），否则多选框找不到选项、只显示原始数字
  const PAY_STATE_DELIVERED = "3";
  const PAY_STATE_REFUNDED = "-2";

  export default {
    name: "SearchOrder",
    components: { VtmFilterBar },
    props: {
      search: Object,
      dateTime: Array,
      payStateList: Array,
      payTypeList: Array
    },
    data() {
      return {
        pickerOptions: {
          shortcuts: [
            {
              text: this.$t("TimeControl.week"),
              onClick(picker) {
                const end = new Date();
                end.setHours(23, 59, 59, 999);
                const start = new Date();
                start.setTime(start.getTime() - 3600 * 1000 * 24 * 7);
                picker.$emit("pick", [start, end]);
              }
            },
            {
              text: this.$t("TimeControl.month"),
              onClick(picker) {
                const end = new Date();
                end.setHours(23, 59, 59, 999);
                const start = new Date();
                start.setTime(start.getTime() - 3600 * 1000 * 24 * 30);
                picker.$emit("pick", [start, end]);
              }
            },
            {
              text: this.$t("TimeControl.year"),
              onClick(picker) {
                const end = new Date();
                end.setHours(23, 59, 59, 999);
                const start = new Date();
                start.setTime(start.getTime() - 3600 * 1000 * 24 * 90);
                picker.$emit("pick", [start, end]);
              }
            }
          ]
        },
        exportLoading: false
      };
    },
    computed: {
      // 可叠加检索的字段（映射到后端 order/page 参数）
      fields() {
        return [
          { value: "equipmentName", label: this.$t("searchOrder.equipmentName"), list: "equipmentNameList" },
          { value: "orderNumber", label: this.$t("searchOrder.orderNumber") },
          { value: "userName", label: this.$t("searchOrder.userName") },
          { value: "equipmentNo", label: this.$t("searchOrder.equipmentNo"), list: "equipmentNoList" },
          { value: "goodsName", label: this.$t("order.goodsName") }
        ];
      },
      // 运营最常用的报表口径
      presets() {
        return [
          {
            id: "deliveredThisMonth",
            name: this.$t("searchOrder.presetDeliveredThisMonth"),
            set: { payStates: [PAY_STATE_DELIVERED] },
            range: "thisMonth"
          },
          {
            id: "deliveredLastMonth",
            name: this.$t("searchOrder.presetDeliveredLastMonth"),
            set: { payStates: [PAY_STATE_DELIVERED] },
            range: "lastMonth"
          },
          {
            id: "refunded30",
            name: this.$t("searchOrder.presetRefunded30"),
            set: { payStates: [PAY_STATE_REFUNDED] },
            range: "last30"
          }
        ];
      }
    },
    methods: {
      onSubmit() {
        // 日期区间同步进检索条件
        if (this.$refs.fb) this.$refs.fb.syncFilters();
        if (this.dateTime && this.dateTime.length > 0) {
          this.search.startTime = this.$timeUtil.formatTime(this.dateTime[0]);
          this.search.endTime = this.$timeUtil.formatTime(this.dateTime[1]);
        } else {
          this.search.startTime = "";
          this.search.endTime = "";
        }
        this.$emit("onSubmit", this.search);
      },
      resetSearch() {
        // 多选字段清成 []（清成 "" 会让后端解析失败，导出报错）
        Object.keys(this.search).forEach(k => {
          this.search[k] = Array.isArray(this.search[k]) ? [] : "";
        });
        // 日期回到默认近 7 天，避免选择器显示有区间而实际查的是全部时间
        if (this.dateTime) {
          const end = new Date();
          const start = new Date(end.getTime() - 7 * 24 * 60 * 60 * 1000);
          this.dateTime.splice(0, this.dateTime.length, start, end);
        }
        this.onSubmit();
      },
      exportOrder() {
        // 导出与列表同条件（含所有生效标签）
        if (this.$refs.fb) this.$refs.fb.syncFilters();
        this.$confirm(this.$t("searchOrder.exportConfirm"), this.$t("common.export"), {
          confirmButtonText: this.$t("common.definite"),
          cancelButtonText: this.$t("common.cancel")
        })
          .then(() => {
            this.exportLoading = true;
            this.$api.download(this.$url.Order.export, this.search).finally(() => {
              this.exportLoading = false;
            });
          })
          .catch(err => {
            if (err.msg) {
              this.$message.error(err.msg);
            }
          });
      }
    }
  };
</script>

<style scoped lang="scss">
  .so-export {
    flex-shrink: 0;
  }

  .so-date {
    flex-shrink: 0;
    width: 360px !important;
  }

  .so-select {
    flex-shrink: 0;
    width: 150px;
  }

  .search-order .el-search {
    overflow: visible;
  }
</style>
